import React from "react";
import styled from "styled-components";
import { string, bool } from "prop-types";
import { motion } from "framer-motion";
import { useTranslation } from "@lib/i18n";
import { useRouter } from "next/router";

import ProgressBar from "../progressBar/ProgressBar";
import ContentContainer from "./ContentContainer";
import ThankYouSectionBanner from "../ThankYouSectionBanner";
import Typography from "@components/shared/Typography";

import { tablet } from "@components/shared/utils";
import { ROUTES } from "@shared/routing/routes";
import { useConfigurationPathname } from "@hooks/useConfigurationPathname";

import gtm from "@gtm/core";
import {
  finalTestDriveRedirectEvent,
  testDriveConfirmationUpdateUrlEvent,
} from "@gtm/events/testdrive";

import { useTestDrivePointSelector } from "@redux/reducers/testDrive";
import { useEffect } from "react";

const ThankYouContainer = styled(motion.div)`
  margin 2.2rem 0;
`;

const InfoSection = styled(motion.div)`
  ${tablet`
    margin-left: 5rem;
  `}
`;

const FinalStepContainer = styled.div`
  max-width: 60rem;
  overflow: hidden;
`;

const FinalStep = ({
  affiliation,
  gitId,
  progressBarColor,
  testDriveType,
  isSSRRendered,
}) => {
  const { t } = useTranslation();
  const { zipcode } = useTestDrivePointSelector();
  const router = useRouter();
  const configurationPathname = useConfigurationPathname();

  useEffect(() => {
    gtm.fire(
      testDriveConfirmationUpdateUrlEvent({
        type: testDriveType,
        gitId,
        postalCode: zipcode,
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );
  }, []);

  const handleThankYouButtonClick = () => {
    gtm.fire(finalTestDriveRedirectEvent());

    router.push(ROUTES.CONFIG, configurationPathname);
  };

  return (
    <FinalStepContainer>
      <section>
        <ProgressBar
          progressBarColor={progressBarColor}
          isFinalStep
          isComplete
        />
        <ContentContainer
          title={t("testDrive.merciSection.title")}
          progressBarColor={progressBarColor}
        >
          <ThankYouContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <Typography
              bold
              uppercase
              forwardedAs="h2"
              size="lg"
              data-id="test-drive-thank-you"
            >
              {t("testDrive.merciSection.headline")}
            </Typography>
            <Typography size="md">
              {t("testDrive.merciSection.text1")}
            </Typography>
          </ThankYouContainer>
        </ContentContainer>
      </section>
      <InfoSection
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        transition={{ delay: 1.4, duration: 0.3 }}
      >
        <ThankYouSectionBanner
          onThankYouButtonClick={handleThankYouButtonClick}
        />
      </InfoSection>
    </FinalStepContainer>
  );
};

FinalStep.propTypes = {
  affiliation: string,
  gitId: string,
  progressBarColor: string,
  testDriveType: string.isRequired,
  isSSRRendered: bool,
};

export default FinalStep;
